/**
 * The theme control: what the player has chosen, and what pressing it does.
 *
 * The palette itself lives in `app.css` and is selected by a `data-theme`
 * attribute, which `index.html` puts there before the first paint. This keeps
 * that attribute in step afterwards, follows the system while the player has
 * not chosen yet, and remembers what they chose.
 */

import {
  applyPalette,
  describeTheme,
  type Palette,
  storeChoice,
  storedChoice,
  systemPalette,
} from '../lib/theme';

class Theme {
  /** What the player chose, or nothing while the system decides. */
  choice = $state<Palette | null>(storedChoice());
  /** What the system prefers, which is what shows until they choose. */
  system = $state<Palette>(systemPalette());

  constructor() {
    applyPalette(this.palette);
    if (typeof matchMedia === 'function') {
      // A system that changes its mind while the page is open only matters
      // while the player has not chosen for themselves.
      matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
        this.system = systemPalette();
        if (this.choice === null) {
          applyPalette(this.palette);
        }
      });
    }
  }

  /** The palette on screen. */
  get palette(): Palette {
    return this.choice ?? this.system;
  }

  /** The tooltip: what is on, and what the next press will bring. */
  get title(): string {
    return describeTheme(this.palette);
  }

  /** Switches to the other palette, and remembers it. */
  toggle(): void {
    this.choice = this.palette === 'light' ? 'dark' : 'light';
    storeChoice(this.choice);
    applyPalette(this.choice);
  }
}

export const theme = new Theme();
